import { useCallback, useMemo, useRef, useState } from 'react';
import { createStressTest } from '../api';
import type { StressOptions, StressTestCreatedResponse } from '../types';
import { parseScanCsv } from './csv';
import { endpointLabel, formatSeconds } from './format';

interface StressTestFormProps {
  options: StressOptions;
  onCreated: (created: StressTestCreatedResponse) => void;
}

const numberField =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200';

export function StressTestForm({ options, onCreated }: StressTestFormProps) {
  const [name, setName] = useState('');
  const [modelType, setModelType] = useState(options.default_model_type);
  const [totalCalls, setTotalCalls] = useState(20);
  const [burstSize, setBurstSize] = useState(5);
  const [burstDelay, setBurstDelay] = useState(2);
  const [imageWidth, setImageWidth] = useState(1024);
  const [imageHeight, setImageHeight] = useState(768);
  const [triggerBatch, setTriggerBatch] = useState(true);
  const [scanIds, setScanIds] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bursts = Math.ceil(totalCalls / Math.max(1, Math.min(burstSize, totalCalls)));
  const scheduleSeconds = Math.max(0, bursts - 1) * burstDelay;
  const randomized = modelType === options.random_model_type;

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setScanIds(parseScanCsv(String(e.target?.result || '')));
    reader.readAsText(file);
  }, []);

  const clearCsv = useCallback(() => {
    setScanIds([]);
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      onCreated(
        await createStressTest({
          name: name.trim() || undefined,
          filename: fileName || undefined,
          scan_ids: scanIds,
          model_type: modelType,
          total_calls: totalCalls,
          burst_size: burstSize,
          burst_delay_seconds: burstDelay,
          image_width: imageWidth,
          image_height: imageHeight,
          trigger_lightning_batch: triggerBatch,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const notices = useMemo(() => {
    const list: string[] = [];
    if (!options.lightning_configured && triggerBatch) {
      list.push(
        'The lightning backend is not reachable from apigen (APIGEN_LIGHTNING_BASE_URL is unset), ' +
          'so no batch run will be started.',
      );
    }
    if (!options.async_submits_supported) {
      list.push(
        'S3 is not configured, so calls are submitted synchronously — each burst holds its ' +
          'connections open for the whole (simulated) generation.',
      );
    }
    return list;
  }, [options, triggerBatch]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
      <h2 className="text-base font-semibold text-slate-900">New stress test</h2>
      <p className="text-sm text-slate-500 mt-1">
        Fires {totalCalls} gen-api calls
        {randomized ? ' spread across every endpoint' : ` at ${endpointLabel(modelType)}`}, in{' '}
        {bursts} burst{bursts === 1 ? '' : 's'} of {burstSize},
        {burstDelay > 0 ? ` ${burstDelay}s apart` : ' back to back'}
        {scheduleSeconds > 0 ? ` (${formatSeconds(scheduleSeconds)} of scheduling)` : ''}. Every call
        runs in dummy mode, so nothing is bought.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. peak-hour simulation"
            className={`mt-1 ${numberField}`}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Endpoint</span>
          <select
            value={modelType}
            onChange={(e) => setModelType(e.target.value)}
            className={`mt-1 ${numberField}`}
          >
            {options.model_types.map((key) => (
              <option key={key} value={key}>
                {key === options.random_model_type
                  ? 'Randomized — every endpoint'
                  : endpointLabel(key)}
              </option>
            ))}
          </select>
          <span className="block text-xs text-slate-500 mt-1">
            {randomized
              ? 'Calls are dealt from a shuffled deck of all endpoints, so the mix stays even and every endpoint is covered.'
              : 'Every call hits this one endpoint.'}
          </span>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Total API calls</span>
          <input
            type="number"
            min={1}
            max={options.max_total_calls}
            value={totalCalls}
            onChange={(e) => setTotalCalls(Number(e.target.value))}
            className={`mt-1 ${numberField}`}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">
            Burst size (fired simultaneously)
          </span>
          <input
            type="number"
            min={1}
            max={options.max_burst_size}
            value={burstSize}
            onChange={(e) => setBurstSize(Number(e.target.value))}
            className={`mt-1 ${numberField}`}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Delay between bursts (seconds)</span>
          <input
            type="number"
            min={0}
            step={0.5}
            max={options.max_burst_delay_seconds}
            value={burstDelay}
            onChange={(e) => setBurstDelay(Number(e.target.value))}
            className={`mt-1 ${numberField}`}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Image width</span>
            <input
              type="number"
              min={64}
              max={options.max_image_edge}
              value={imageWidth}
              onChange={(e) => setImageWidth(Number(e.target.value))}
              className={`mt-1 ${numberField}`}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Image height</span>
            <input
              type="number"
              min={64}
              max={options.max_image_edge}
              value={imageHeight}
              onChange={(e) => setImageHeight(Number(e.target.value))}
              className={`mt-1 ${numberField}`}
            />
          </label>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600">Scans CSV</span>
          {scanIds.length > 0 && (
            <button
              onClick={clearCsv}
              className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="relative mt-1">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
            {scanIds.length > 0 ? (
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{scanIds.length}</span> scan IDs
                loaded
                {fileName && <span className="text-xs text-slate-400 font-mono"> ({fileName})</span>}
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-700">
                  Drop a CSV here or <span className="text-indigo-600 font-medium">browse</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  One scan ID per line, or an export with a scan_id column
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={triggerBatch}
          onChange={(e) => setTriggerBatch(e.target.checked)}
          className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
        />
        <span className="text-sm text-slate-700">
          Also run these scans as a lightning batch
          <span className="block text-xs text-slate-500">
            Loads the merch pipeline and the shared database alongside the gen-api calls. Requires
            lightning to be in dummy mode, or the batch will cost real money.
          </span>
        </span>
      </label>

      {notices.map((notice) => (
        <div
          key={notice}
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-xs"
        >
          {notice}
        </div>
      ))}

      {error && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={submitting}
        className="mt-5 w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? 'Starting…' : 'Start stress test'}
      </button>
    </div>
  );
}
